import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import apiClient from "../services/apiClient";

/**
 * Day Reports Store - Simplified and optimized
 *
 * Inherits from base store:
 * - CRUD operations (create, update, destroy, fetchById)
 * - Caching with 5-minute TTL
 * - Real-time updates via ActionCable
 * - Pagination support
 *
 * Day reports specific features:
 * - Monthly reports for Planner component
 * - Backward compatibility with legacy method names
 * - Array synchronization between base store and legacy arrays
 *
 * Note: Search functionality moved to dayReportSearchStore.js
 */

// Create the base store with day reports configuration
const dayReportsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/day_reports",
  name: "DayReportsStoreRefactor",
  objectName: "Dagrapport", // Auto-generates CRUD success messages
  enableCache: true, // 5-minute cache for day reports
  //actionCableChannel: "DayReportsChannel", // Enable real-time updates
  infiniteLoading: true, // Support pagination
});

export const useDayReportsStoreRefactor = defineStore("dayReportsRefactor", {
  // Inherit state from base store
  state: () => ({
    ...dayReportsBaseStore.state(),
    // Day reports specific state
    dayReports: [], // Backward compatibility array (synced with base store data)
    monthlyReports: [], // Planner-specific monthly data
    currentMonth: null, // Current cached month
    currentYear: null, // Current cached year
    loadingMonthly: false, // Loading state for monthly data
    icons: {
      vrij: "mdi-calendar-check",
      ziek: "mdi-calendar-remove",
      overig: "mdi-calendar-question",
    },
  }),

  getters: {
    ...dayReportsBaseStore.getters,

    // Backward compatibility - use base store getter
    getDayReportById: (state) => (id) =>
      state.data.find((report) => report.id == id), // Same as base getById but keeping for backward compatibility
    getActiveDayReport: (state) => (userId) =>
      state.data.find(
        (report) =>
          report.user_id == userId &&
          report.user?.active_day_report_id == report.id,
      ),

    // Monthly reports getters
    monthlyReportsByDate: (state) => {
      const grouped = {};
      state.monthlyReports.forEach((report) => {
        const date = report.date;
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(report);
      });

      // Sort each day's reports
      Object.keys(grouped).forEach((date) => {
        grouped[date].sort((a, b) => {
          if (a.start_time && b.start_time)
            return a.start_time.localeCompare(b.start_time);
          if (a.start_time && !b.start_time) return -1;
          if (!a.start_time && b.start_time) return 1;
          return a.day_report_type.localeCompare(b.day_report_type);
        });
      });

      return grouped;
    },

    sortedMonthlyDates: (state) =>
      [...new Set(state.monthlyReports.map((report) => report.date))].sort(),
  },

  actions: {
    ...dayReportsBaseStore.actions,

    // Simple utility
    setIcons(icons) {
      this.icons = icons;
    },

    // Backward compatibility - use base store caching
    async getDayReport(id) {
      const result = await this.fetchById(id);
      this.dayReports = this.data.slice(); // Keep backward compatibility array in sync
      return result;
    },

    // CRUD operations - override base store to sync arrays
    async create(dayReport) {
      const result = await dayReportsBaseStore.actions.create.call(
        this,
        dayReport,
      );
      this._syncArrays(result);
      return result;
    },

    async update(dayReport) {
      const result = await dayReportsBaseStore.actions.update.call(
        this,
        dayReport,
      );
      this._syncArrays(result);
      return result;
    },

    async destroy(dayReport) {
      const result = await dayReportsBaseStore.actions.destroy.call(
        this,
        dayReport,
      );
      const id = typeof dayReport === "object" ? dayReport.id : dayReport;
      this.dayReports = this.dayReports.filter((dr) => dr.id !== id);
      this.monthlyReports = this.monthlyReports.filter((dr) => dr.id !== id);
      return result;
    },

    // Day report specific actions
    async open(dayReport) {
      const result = await this.customApiCall(
        "GET",
        `/api/v1/day_reports/${dayReport.id}/open`,
        {},
        "Dagrapport is geopend",
      );
      this._syncArrays(result);
      return result;
    },

    async close(dayReport) {
      const result = await this.customApiCall(
        "GET",
        `/api/v1/day_reports/${dayReport.id}/close`,
        {},
        "Dagrapport is gesloten",
      );
      this._syncArrays(result);
      return result;
    },

    // Monthly reports - use proper API format
    async getMonthlyReports(month, year) {
      try {
        this.loadingMonthly = true;
        const response = await apiClient.get(
          "/api/v1/day_reports/month",
          { params: { month, year } },
          null,
        );

        // Handle new backend response format with data/meta structure
        let results = [];
        if (response?.data && Array.isArray(response.data)) {
          // New format with meta data
          results = response.data;
          console.log(
            "DayReportsStoreRefactor: Received paginated monthly response:",
            {
              dataCount: results.length,
              meta: response.meta,
              status: response.status,
            },
          );
        } else if (Array.isArray(response)) {
          // Legacy format (direct array)
          results = response;
          console.log(
            "DayReportsStoreRefactor: Received legacy monthly response with",
            results.length,
            "items",
          );
        } else {
          console.warn(
            "DayReportsStoreRefactor: Unexpected monthly response format:",
            response,
          );
          results = [];
        }

        this.monthlyReports = results;
        this.currentMonth = month;
        this.currentYear = year;
        return results;
      } catch (error) {
        console.error(
          "DayReportsStoreRefactor: Error in getMonthlyReports:",
          error,
        );
        this.monthlyReports = [];
        return [];
      } finally {
        this.loadingMonthly = false;
      }
    },

    // Override getAll to properly handle pagination for search endpoint
    async getAll(searchParams = {}, append = false) {
      // For append operations, let the base store handle offset management
      if (append) {
        const { offset, ...queryParams } = searchParams;
        return await dayReportsBaseStore.actions.search.call(
          this,
          queryParams,
          append,
        );
      } else {
        // For fresh searches, pass all parameters to base store
        return await dayReportsBaseStore.actions.search.call(
          this,
          searchParams,
          append,
        );
      }
    },

    // Private helper to sync all arrays
    _syncArrays(report) {
      this.dayReports = this.data.slice();

      // For monthly reports, only sync if we have a current month set OR if no specific month filtering is active
      if (report?.date && this.currentMonth && this.currentYear) {
        const reportDate = new Date(report.date);
        const reportMonth = reportDate.getMonth() + 1;
        const reportYear = reportDate.getFullYear();

        if (
          reportMonth === this.currentMonth &&
          reportYear === this.currentYear
        ) {
          const idx = this.monthlyReports.findIndex(
            (dr) => dr.id === report.id,
          );
          if (idx !== -1) {
            this.monthlyReports[idx] = report;
          } else {
            this.monthlyReports.push(report);
          }
        }
      } else if (report?.date && !this.currentMonth) {
        // If no specific month is set, add to monthlyReports for testing
        const idx = this.monthlyReports.findIndex((dr) => dr.id === report.id);
        if (idx !== -1) {
          this.monthlyReports[idx] = report;
        } else {
          this.monthlyReports.push(report);
        }
      }
    },

    // Note: addOrUpdate is inherited from base store and works correctly with _upsertItem override

    // Override base store methods to keep arrays in sync
    _upsertItem(item, prepend = false) {
      dayReportsBaseStore.actions._upsertItem.call(this, item, prepend);
      // Sync arrays after individual item updates (not bulk operations)
      this._syncArrays(item);
    },

    _removeItem(id) {
      dayReportsBaseStore.actions._removeItem.call(this, id);
      this.dayReports = this.dayReports.filter((dr) => dr.id !== id);
      this.monthlyReports = this.monthlyReports.filter((dr) => dr.id !== id);
    },
  },
});
